import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, Menu, MenuItem, Paper, Divider, useMediaQuery, TextField, InputAdornment, } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { getWvBrandingSafe } from '../../../services/wvBrandingConfig';
import { resolvePublicUrl, getStaticBaseUrl } from '../../../utils/publicUrl';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchLandingGrids } from '../../../store/landingGridsSlice';
import { computeLandingUnifiedSearch } from '../../../services/landingUnifiedSearch';
import { applyWvLandingDepartmentHeroImages, mergeDepartmentImagesFromPageJson } from '../../../services/landingDepartmentMerge';
import { readLandingPermitCatalogSearchUrl } from '../../../services/envConfig';
import { getCachedEnvConfig, loadEnvConfig, readAuthorizationLoginOptions, readAuthUILoginEmbedMode, readCustomerLoginUrl, readLogoutUrl, readProfileUrl, readUserRegistrationUrl, } from '../../../services/envConfig';
import { withAuthCallbackUrl } from '../../../utils/authCallbackUrl';
import { clearPortalAuthStorage, hasAuthTokens, hasInternalAuthToken } from '../../../utils/authTokens';
import { getApplicationToken } from '../../library/Utils/auth';
import { getEnvVariable } from '../../../envs';
import { get } from '../../library/api/rest';
import { LoginPortalDialog } from '../../Auth';
import { WvStackedStateWordmark } from '../WvStackedStateWordmark';
import { parseHashRouterNavIntent, scheduleScrollToElementAfterRoute } from '../../../utils/internalPath';
import { PORTAL_SHELL_DEPARTMENT_ID, VIEW_DETAILS_QUERY, rawViewDetailsQueryValue, shouldEmbedDashboardHrefInPortalShell, } from '../../../utils/permitDetailsEmbed';
import { resolveDashboardNav } from '../../../utils/dashboardNav';
import { cmsPortalTypography } from '../../../theme/cmsTypography';
import { portalNavLayoutOffset, portalNavSealHeight, portalNavToolbarMinHeight, } from '../../../theme/portalLayout';
import NavSearch from '../NavSearch/NavSearch';
import { SearchResultsList } from '../NavSearch/SearchResultsList';
import { useLocale } from '../../../locale/LocaleProvider';
/** Lively profile pill: a light sheen sweeps across, then rests before repeating. */
const profileShimmer = keyframes `
  0% { transform: translateX(-150%); }
  55%, 100% { transform: translateX(260%); }
`;
/** Small "active" dot gently pulses (opacity + scale — stays within the clipped pill). */
const profilePulse = keyframes `
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.78); }
`;
/** Icon per dashboard menu row — driven by label so JSON/env defaults still look right. */
function DashboardNavItemIcon({ label }) {
    const l = label.toLowerCase();
    if (l.includes('claim')) {
        return _jsx(AssignmentIndRoundedIcon, { sx: { fontSize: 22 } });
    }
    return _jsx(DashboardRoundedIcon, { sx: { fontSize: 22 } });
}
const NavBar = ({ logoText = 'West Virginia', logoSubtext, links = [], loginLabel, loginHref, ctaLabel, ctaHref, logoutLabel, embedInHero: _embedInHero, beforeLoginLink, dashboardNavLink, searchPlaceholder, 
/** No submit button in the search UI anymore — see `onSearchSubmit` comment below. */
searchButtonLabel: _searchButtonLabel, searchActionUrl, }) => {
    const theme = useTheme();
    const cms = useMemo(() => cmsPortalTypography(theme), [theme]);
    const navigate = useNavigate();
    const sealSrc = getWvBrandingSafe().wvStateSealPng;
    /**
     * The desktop row (search + dashboard + nav links + back-link + login/signup/profile) needs
     * real width to avoid clipping. Font size (`cmsPortalTypography` `navPrimary`), button padding,
     * and the search box width (`NavSearch`) all trade off against this cutoff — widening the
     * search box (per product ask) pushed the safe point back up from 1280px. Verified via
     * screenshots that the worst case (full nav + back-link + Login/Sign Up) fits cleanly from
     * 1320px, but still clips below it. Collapse to the hamburger menu below that.
     */
    const isMobile = useMediaQuery(theme.breakpoints.down(1320));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [authOptions, setAuthOptions] = useState(() => readAuthorizationLoginOptions(getCachedEnvConfig()));
    const [loginDialogOpen, setLoginDialogOpen] = useState(false);
    const [embedLoginUrl, setEmbedLoginUrl] = useState(null);
    const [loggedIn, setLoggedIn] = useState(() => hasAuthTokens());
    const [hasInternalSession, setHasInternalSession] = useState(() => hasInternalAuthToken());
    const [profile, setProfile] = useState(() => {
        try {
            const raw = localStorage.getItem('profile');
            return raw ? JSON.parse(raw) : null;
        }
        catch {
            return null;
        }
    });
    const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
    const [signUpHref, setSignUpHref] = useState(ctaHref ?? '');
    /** Bumps when `env.json` loads so dashboard URLs from env re-resolve. */
    const [envConfigVersion, setEnvConfigVersion] = useState(0);
    const [dashboardMenuAnchor, setDashboardMenuAnchor] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchSuggestOpen, setSearchSuggestOpen] = useState(false);
    const searchAnchorRef = React.useRef(null);
    const dispatch = useAppDispatch();
    const landingGrids = useAppSelector((s) => s.landingGrids);
    const { shell } = useLocale();
    /**
     * No Enter-to-submit — there is no `/search` results route (and `searchActionUrl`, when a CMS
     * page sets one, isn't wired to any UI trigger either). A result must be selected from
     * `SearchResultsList`; see `NavSearch` (desktop) and the mobile drawer below.
     */
    const useInternalSearch = !searchActionUrl?.trim();
    const permitCatalogUrl = useMemo(() => readLandingPermitCatalogSearchUrl(getCachedEnvConfig()), [landingGrids?.status]);
    const searchBuckets = useMemo(() => {
        if (!useInternalSearch)
            return { departments: [], agencies: [], permits: [] };
        const resolvedDepartments = applyWvLandingDepartmentHeroImages(mergeDepartmentImagesFromPageJson(landingGrids?.departmentsRaw ?? [], undefined));
        return computeLandingUnifiedSearch(searchQuery, resolvedDepartments, landingGrids?.agencies ?? [], {
            permitCatalogUrlTemplate: permitCatalogUrl,
            permitCatalogTitle: undefined,
            permitsCatalog: landingGrids?.permitsCatalog ?? [],
        });
    }, [useInternalSearch, searchQuery, landingGrids, permitCatalogUrl]);
    const searchHitsFlat = useMemo(() => [...searchBuckets.departments, ...searchBuckets.agencies, ...searchBuckets.permits], [searchBuckets]);
    const location = useLocation();
    const isCompactSearch = useMemo(() => {
        const p = location.pathname.toLowerCase();
        return p.includes('/dashboard') || p.includes('/claim') || p.includes('/claims') || p.includes('/citizen');
    }, [location.pathname]);
    useEffect(() => {
        if (!useInternalSearch)
            return undefined;
        if (searchQuery.trim().length >= 1)
            setSearchSuggestOpen(true);
        else
            setSearchSuggestOpen(false);
        return undefined;
    }, [searchQuery, useInternalSearch]);
    useEffect(() => {
        const syncEnv = () => {
            const env = getCachedEnvConfig();
            setAuthOptions(readAuthorizationLoginOptions(env));
            const reg = readUserRegistrationUrl(env);
            setSignUpHref(reg || ctaHref || '');
            setEnvConfigVersion((v) => v + 1);
        };
        syncEnv();
        void loadEnvConfig().finally(syncEnv);
    }, [ctaHref]);
    useEffect(() => {
        if (!landingGrids || landingGrids.status === 'idle') {
            void dispatch(fetchLandingGrids());
        }
    }, [dispatch, landingGrids?.status]);
    useEffect(() => {
        const syncAuthState = () => {
            setLoggedIn(hasAuthTokens());
            setHasInternalSession(hasInternalAuthToken());
        };
        window.addEventListener('storage', syncAuthState);
        window.addEventListener('focus', syncAuthState);
        return () => {
            window.removeEventListener('storage', syncAuthState);
            window.removeEventListener('focus', syncAuthState);
        };
    }, []);
    // When auth tokens are present (user logged in), load profile and persist to localStorage
    useEffect(() => {
        if (!loggedIn)
            return;
        const profileLoadTokenRef = NavBar._profileLoadTokenRef || React.createRef();
        if (!NavBar._profileLoadTokenRef)
            NavBar._profileLoadTokenRef = profileLoadTokenRef;
        const loadProfile = async () => {
            try {
                // Ensure an application token is present before calling profile endpoint
                const token = getApplicationToken();
                if (!token) {
                    return;
                }
                // Prevent duplicate fetches for the same token (React StrictMode can double-run effects)
                if (profileLoadTokenRef.current === token) {
                    // Already requested for this token
                    return;
                }
                profileLoadTokenRef.current = token;
                const admin = String(getEnvVariable('Admin') || '');
                if (!admin)
                    return;
                const base = admin.endsWith('/') ? admin.slice(0, -1) : admin;
                const url = `${base}/api/Users/Profile`;
                const data = await get(url, null, null, null);
                if (data) {
                    setProfile(data);
                    try {
                        localStorage.setItem('profile', JSON.stringify(data));
                    }
                    catch {
                        /* ignore storage errors */
                    }
                }
            }
            catch (err) {
                console.log('Failed to load profile', err);
            }
        };
        void loadProfile();
    }, [loggedIn]);
    const logoutButtonLabel = logoutLabel?.trim() || 'Logout';
    /** Full display name for the logged-in user's profile menu (from /api/Users/Profile). */
    const profileName = useMemo(() => (profile ? [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim() : ''), [profile]);
    /** Navigates to the IdP profile page (`env.json` `ProfileUrl`) in a new tab. */
    const handleOpenProfile = useCallback(() => {
        setDrawerOpen(false);
        setProfileMenuAnchor(null);
        const profileUrl = readProfileUrl(getCachedEnvConfig());
        if (profileUrl)
            window.open(profileUrl, '_blank', 'noopener,noreferrer');
    }, []);
    const handleLogout = useCallback(() => {
        setDrawerOpen(false);
        setProfileMenuAnchor(null);
        setProfile(null);
        const logoutUrl = readLogoutUrl(getCachedEnvConfig()) || getStaticBaseUrl();
        try {
            localStorage.removeItem('profile');
        }
        catch {
            /* ignore */
        }
        clearPortalAuthStorage();
        window.location.assign(logoutUrl);
    }, []);
    const openLoginDialog = useCallback(() => {
        setEmbedLoginUrl(null);
        setLoginDialogOpen(true);
    }, []);
    const closeLoginDialog = useCallback(() => {
        setLoginDialogOpen(false);
        setEmbedLoginUrl(null);
    }, []);
    const preferredCustomerLoginUrl = useCallback(() => {
        const env = getCachedEnvConfig();
        return readCustomerLoginUrl(env) || authOptions[0]?.url || loginHref || '';
    }, [authOptions, loginHref]);
    const startLogin = useCallback((url) => {
        const env = getCachedEnvConfig();
        const mode = readAuthUILoginEmbedMode(env);
        const target = withAuthCallbackUrl(url);
        if (mode === 'redirect') {
            window.location.assign(target);
            return;
        }
        if (mode === 'popup') {
            window.open(target, 'onestop-auth', 'width=520,height=720,noopener,noreferrer');
            closeLoginDialog();
            return;
        }
        setEmbedLoginUrl(target);
    }, [closeLoginDialog]);
    const handleLoginNavClick = useCallback((e) => {
        const customerLoginUrl = preferredCustomerLoginUrl();
        if (customerLoginUrl) {
            e.preventDefault();
            startLogin(customerLoginUrl);
            return;
        }
        if (loginHref) {
            e.preventDefault();
            window.location.assign(withAuthCallbackUrl(loginHref));
        }
    }, [loginHref, preferredCustomerLoginUrl, startLogin]);
    const showLogin = !loggedIn && (Boolean(loginLabel) || authOptions.length > 0 || Boolean(loginHref));
    const loginButtonLabel = loginLabel?.trim() || 'Login';
    const runNavIntent = useCallback((href) => {
        const intent = parseHashRouterNavIntent(href);
        if (intent.kind === 'home') {
            navigate({ pathname: '/' }, { replace: false });
            if (intent.scrollToId)
                scheduleScrollToElementAfterRoute(intent.scrollToId);
            return;
        }
        if (intent.kind === 'app') {
            navigate({ pathname: intent.pathname, search: intent.search }, { replace: false });
            return;
        }
        window.location.assign(intent.href);
    }, [navigate]);
    /** Dashboard dropdown / single button — external citizen or flow URLs stay in shell (same as permit details iframe). */
    const runDashboardNav = useCallback((href) => {
        const env = getCachedEnvConfig();
        if (shouldEmbedDashboardHrefInPortalShell(href, env)) {
            const qs = new URLSearchParams();
            qs.set(VIEW_DETAILS_QUERY, rawViewDetailsQueryValue(href.trim()));
            navigate({
                pathname: `/permits/${PORTAL_SHELL_DEPARTMENT_ID}/details`,
                search: qs.toString(),
            }, { replace: false });
            setDashboardMenuAnchor(null);
            return;
        }
        runNavIntent(href);
        setDashboardMenuAnchor(null);
    }, [navigate, runNavIntent]);
    const centerNavLinks = useMemo(() => links ?? [], [links]);
    /**
     * Active-nav highlight via scroll-spy: the center links are section anchors (#hero, #agencies, …), so the
     * link whose section is currently in view gets highlighted (top of page = Home).
     */
    const [activeSection, setActiveSection] = useState('');
    /** Section pinned by a click — it stays highlighted (spy paused) until the user genuinely scrolls, so the
     *  highlight jumps straight to the clicked link instead of sweeping through sections during smooth scroll. */
    const clickPinnedSectionRef = React.useRef('');
    const navSectionIds = useMemo(() => {
        const ids = [];
        for (const l of centerNavLinks) {
            const intent = parseHashRouterNavIntent(l.href || '');
            if (intent.kind === 'home' && intent.scrollToId)
                ids.push(intent.scrollToId);
        }
        return ids;
    }, [centerNavLinks]);
    useEffect(() => {
        if (!navSectionIds.length) {
            setActiveSection('');
            return;
        }
        // Active = the LAST section whose top has scrolled above a line just below the navbar (~120px), i.e. the
        // section currently at the top of the reading area. Robust for short sections (Agencies) and unaffected
        // by neighbouring section heights.
        const LINE = 120;
        let raf = 0;
        const compute = () => {
            raf = 0;
            if (clickPinnedSectionRef.current)
                return; // a clicked link stays highlighted until the user scrolls
            let bestId = '';
            let bestTop = -Infinity;
            let firstId = '';
            for (const id of navSectionIds) {
                const el = document.getElementById(id);
                if (!el)
                    continue;
                if (!firstId)
                    firstId = id;
                const top = el.getBoundingClientRect().top;
                if (top <= LINE && top > bestTop) {
                    bestTop = top;
                    bestId = id;
                }
            }
            setActiveSection(bestId || firstId);
        };
        const onScroll = () => {
            if (!raf)
                raf = requestAnimationFrame(compute);
        };
        // A genuine user scroll (wheel / touch / keyboard) releases the click-pin so the spy resumes. Programmatic
        // smooth-scroll fires 'scroll' but NOT these, so a click's own scroll won't unpin the target early.
        const onUserIntent = () => {
            clickPinnedSectionRef.current = '';
            onScroll();
        };
        compute();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        window.addEventListener('wheel', onUserIntent, { passive: true });
        window.addEventListener('touchstart', onUserIntent, { passive: true });
        window.addEventListener('keydown', onUserIntent);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            window.removeEventListener('wheel', onUserIntent);
            window.removeEventListener('touchstart', onUserIntent);
            window.removeEventListener('keydown', onUserIntent);
            if (raf)
                cancelAnimationFrame(raf);
        };
    }, [navSectionIds, location.pathname]);
    const isNavLinkActive = useCallback((href) => {
        const intent = parseHashRouterNavIntent(href || '');
        return intent.kind === 'home' && !!intent.scrollToId && intent.scrollToId === activeSection;
    }, [activeSection]);
    /** Click a section link: highlight the target immediately + mute the spy for the smooth-scroll window, so
     *  the highlight jumps straight there instead of sweeping through every section along the way. */
    const handleSectionNavClick = useCallback((href) => {
        const intent = parseHashRouterNavIntent(href || '');
        if (intent.kind === 'home' && intent.scrollToId) {
            setActiveSection(intent.scrollToId);
            clickPinnedSectionRef.current = intent.scrollToId;
        }
        runNavIntent(href);
    }, [runNavIntent]);
    /** Active (in-view) nav link — styled like the old Dashboard gold gradient pill. */
    const activeNavSx = useMemo(() => ({
        color: theme.palette.primary.dark,
        fontWeight: 700,
        px: 1.9,
        py: 0.4,
        minHeight: 32,
        borderRadius: 999,
        border: `1px solid ${alpha(theme.palette.secondary.light, 0.7)}`,
        background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
        boxShadow: `0 3px 10px ${alpha(theme.palette.secondary.dark, 0.35)}`,
        '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            boxShadow: `0 5px 16px ${alpha(theme.palette.secondary.dark, 0.45)}`,
            color: theme.palette.primary.dark,
        },
    }), [theme]);
    const dashboardResolved = useMemo(() => {
        const resolved = resolveDashboardNav(dashboardNavLink, getCachedEnvConfig());
        if (!resolved)
            return resolved;
        // Hide "Claim My Record" — the Dashboard should point straight to the Citizen Dashboard. Dropping it
        // leaves a single item, so the button below renders as a direct link (no dropdown).
        const items = resolved.items.filter((it) => !/claim/i.test(it.label));
        return { ...resolved, items };
    }, [dashboardNavLink, envConfigVersion]);
    const showDashboard = loggedIn && Boolean(dashboardResolved && dashboardResolved.items.length > 0);
    /** Dashboard highlights by ROUTE (not scroll): active when on the portal-shell details route whose
     *  `viewDetails` target matches a dashboard item (e.g. the Citizen Dashboard). */
    const isDashboardActive = useMemo(() => {
        if (!dashboardResolved || dashboardResolved.items.length === 0)
            return false;
        if (!location.pathname.startsWith(`/permits/${PORTAL_SHELL_DEPARTMENT_ID}/details`))
            return false;
        const vd = (new URLSearchParams(location.search).get(VIEW_DETAILS_QUERY) || '').trim();
        if (!vd)
            return false;
        return dashboardResolved.items.some((it) => it.href && it.href.trim() === vd);
    }, [dashboardResolved, location.pathname, location.search]);
    const navButtonSx = useMemo(() => ({
        ...cms.navPrimary,
        color: '#ffffff',
        px: 0.85,
        py: 0.3,
        minHeight: 30,
        whiteSpace: 'nowrap',
        position: 'relative',
        '&:hover': {
            bgcolor: 'rgba(255,255,255,0.1)',
            color: '#ffffff',
        },
    }), [cms]);
    /** Dashboard action — plain nav button (not highlighted); only the in-view section link is highlighted. */
    const dashboardButtonSx = useMemo(() => ({
        ...cms.navPrimary,
        color: '#ffffff',
        px: 0.85,
        py: 0.3,
        minHeight: 30,
        whiteSpace: 'nowrap',
        position: 'relative',
        '& .MuiButton-endIcon': { ml: 0.25 },
        '&:hover': {
            bgcolor: 'rgba(255,255,255,0.1)',
            color: '#ffffff',
        },
    }), [cms]);
    /** Matches AppBar vertical footprint (padding + toolbar minHeight + safe area). */
    const layoutOffset = portalNavLayoutOffset(theme);
    return (_jsxs(_Fragment, { children: [_jsx(AppBar, { position: "fixed", elevation: 0, sx: {
                    top: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                    zIndex: (t) => t.zIndex.appBar,
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    // pt: {
                    //   xs: `calc(${theme.spacing(1)} + env(safe-area-inset-top, 0px))`,
                    //   md: `calc(${theme.spacing(1.5)} + env(safe-area-inset-top, 0px))`,
                    // },
                    // remove page horizontal padding so AppBar spans full viewport
                    pointerEvents: 'none',
                }, children: _jsxs(Toolbar, { sx: {
                        position: 'relative',
                        width: '100%',
                        px: 0,
                        minHeight: portalNavToolbarMinHeight,
                        bgcolor: theme.palette.primary.main,
                        borderRadius: 0,
                        boxShadow: '0 4px 24px rgba(12, 38, 70, 0.22)',
                        pointerEvents: 'auto',
                        gap: { xs: 0.75, md: 1 },
                        flexWrap: 'nowrap',
                    }, children: [_jsx(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0,
                                minWidth: 0,
                            }, children: _jsxs(Box, { component: RouterLink, to: "/", sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    minWidth: 0,
                                }, children: [_jsx(Box, { sx: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }, children: _jsx(Box, { sx: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: portalNavSealHeight }, children: _jsx(Box, { component: "img", src: resolvePublicUrl(sealSrc) ?? sealSrc, alt: "", sx: {
                                                    height: portalNavSealHeight,
                                                    width: 'auto',
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                } }) }) }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(WvStackedStateWordmark, { text: logoText, size: "navbar" }), logoSubtext && (_jsx(Typography, { variant: "caption", sx: {
                                                    ...cms.navLogoSubline,
                                                    color: 'rgba(255,255,255,0.75)',
                                                    lineHeight: 1,
                                                    display: 'block',
                                                }, children: logoSubtext }))] })] }) }), !isMobile && (showDashboard || centerNavLinks.length > 0) ? (_jsxs(Box, { sx: {
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0,
                                px: 1,
                                overflow: 'hidden',
                            }, children: [_jsx(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mr: 2,
                                        ml: { lg: 'clamp(1rem, 2.5vw, 2.5rem)', xl: 'clamp(1.5rem, 3vw, 3.5rem)' },
                                        flex: '1 1 100%',
                                    }, children: _jsx(NavSearch, { compact: isCompactSearch, searchQuery: searchQuery, setSearchQuery: setSearchQuery, searchBuckets: searchBuckets, searchSuggestOpen: searchSuggestOpen, setSearchSuggestOpen: setSearchSuggestOpen, searchPlaceholder: searchPlaceholder, onNavigate: (href) => runNavIntent(href) }) }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto', flexShrink: 0 }, children: [showDashboard && dashboardResolved ? (dashboardResolved.items.length === 1 ? (_jsx(Button, { type: "button", onClick: () => runDashboardNav(dashboardResolved.items[0].href), sx: isDashboardActive ? { ...dashboardButtonSx, ...activeNavSx } : dashboardButtonSx, children: dashboardResolved.label })) : (_jsxs(_Fragment, { children: [_jsx(Button, { type: "button", "aria-haspopup": "menu", "aria-expanded": Boolean(dashboardMenuAnchor), endIcon: _jsx(ArrowDropDownIcon, { sx: { fontSize: 18 } }), onClick: (e) => setDashboardMenuAnchor(e.currentTarget), sx: isDashboardActive ? { ...dashboardButtonSx, ...activeNavSx } : dashboardButtonSx, children: dashboardResolved.label }), _jsx(Menu, { anchorEl: dashboardMenuAnchor, open: Boolean(dashboardMenuAnchor), onClose: () => setDashboardMenuAnchor(null), 
                                                    /** Avoid body scroll-lock (removes scrollbar) — that was shifting the page left. */
                                                    disableScrollLock: true, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, transformOrigin: { vertical: 'top', horizontal: 'center' }, transitionDuration: 150, PaperProps: {
                                                        sx: {
                                                            minWidth: 272,
                                                            mt: 1,
                                                            py: 0.75,
                                                            px: 0.5,
                                                            borderRadius: 2,
                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                                                            boxShadow: `0 14px 44px ${alpha(theme.palette.primary.dark, 0.16)}`,
                                                            bgcolor: alpha(theme.palette.common.white, 0.98),
                                                            backdropFilter: 'blur(8px)',
                                                        },
                                                    }, MenuListProps: { dense: false, sx: { py: 0 } }, children: dashboardResolved.items.map((it) => (_jsxs(MenuItem, { onClick: () => runDashboardNav(it.href), sx: {
                                                            gap: 1.25,
                                                            py: 1.15,
                                                            px: 1.25,
                                                            mx: 0.25,
                                                            mb: 0.25,
                                                            borderRadius: 1.25,
                                                            alignItems: 'center',
                                                            typography: 'body2',
                                                            fontWeight: 700,
                                                            color: 'primary.dark',
                                                            letterSpacing: '-0.01em',
                                                            '&:last-of-type': { mb: 0 },
                                                            '&:hover, &:focus-visible': {
                                                                bgcolor: alpha(theme.palette.primary.main, 0.09),
                                                            },
                                                        }, children: [_jsx(Box, { "aria-hidden": true, sx: {
                                                                    flexShrink: 0,
                                                                    width: 40,
                                                                    height: 40,
                                                                    borderRadius: 1.25,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                                    color: 'primary.main',
                                                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                                                                }, children: _jsx(DashboardNavItemIcon, { label: it.label }) }), _jsx(Typography, { variant: "body2", sx: { fontWeight: 700, lineHeight: 1.35, flex: 1, minWidth: 0 }, children: it.label })] }, it.href))) })] }))) : null, centerNavLinks.map((link, i) => (_jsx(Button, { component: "button", type: "button", "aria-current": isNavLinkActive(link.href) ? 'page' : undefined, onClick: () => handleSectionNavClick(link.href), sx: isNavLinkActive(link.href) ? { ...navButtonSx, ...activeNavSx } : navButtonSx, children: link.label }, i)))] })] })) : (!isMobile && _jsx(Box, { sx: { flex: 1, minWidth: 0 } })), _jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexShrink: 0,
                                ml: isMobile ? 'auto' : 0,
                            }, children: [beforeLoginLink && (_jsx(Button, { component: RouterLink, to: beforeLoginLink.href, startIcon: _jsx(ArrowBackIcon, { sx: { fontSize: 18 } }), sx: {
                                        ...cms.navPrimary,
                                        color: '#ffffff',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        px: 1,
                                        py: 0.3,
                                        minHeight: 30,
                                        mr: 0.25,
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.12)',
                                            color: '#ffffff',
                                        },
                                    }, children: beforeLoginLink.label })), showLogin && !isMobile && (_jsx(Button, { ...(authOptions.length === 0
                                        ? { href: withAuthCallbackUrl(loginHref || '#') }
                                        : { type: 'button' }), onClick: authOptions.length > 0 ? handleLoginNavClick : undefined, variant: "contained", size: "medium", sx: {
                                        ...cms.navPrimary,
                                        fontWeight: 700,
                                        px: 1.5,
                                        py: 0.35,
                                        minHeight: 32,
                                        borderRadius: 999,
                                        mr: 0.35,
                                        bgcolor: '#0f9615',
                                        color: '#ffffff',
                                        '&:hover': {
                                            bgcolor: '#2e7d32',
                                        },
                                    }, children: loginButtonLabel })), ctaLabel && !loggedIn && !isMobile && (_jsx(Button, { href: signUpHref ? withAuthCallbackUrl(signUpHref) : '#', variant: "contained", color: "secondary", size: "medium", sx: {
                                        ...cms.navPrimary,
                                        fontWeight: 700,
                                        px: 1.5,
                                        py: 0.35,
                                        minHeight: 32,
                                        borderRadius: 999,
                                    }, children: ctaLabel })), loggedIn && profile && !isMobile && (_jsxs(_Fragment, { children: [_jsxs(Button, { type: "button", "aria-haspopup": "menu", "aria-expanded": Boolean(profileMenuAnchor), onClick: (e) => setProfileMenuAnchor(e.currentTarget), endIcon: _jsx(ArrowDropDownIcon, { sx: { fontSize: 20 } }), sx: {
                                                ...cms.navPrimary,
                                                position: 'relative',
                                                overflow: 'hidden',
                                                color: '#ffffff',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                px: 1.4,
                                                py: 0.4,
                                                minHeight: 32,
                                                maxWidth: 240,
                                                borderRadius: 999,
                                                bgcolor: alpha('#ffffff', 0.16),
                                                border: `1px solid ${alpha('#ffffff', 0.4)}`,
                                                backdropFilter: 'blur(6px)',
                                                WebkitBackdropFilter: 'blur(6px)',
                                                boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.25)}`,
                                                transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
                                                '& .MuiButton-endIcon': { ml: 0.25 },
                                                /* Lively sheen sweeping across the frosted pill. */
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '45%',
                                                    height: '100%',
                                                    background: `linear-gradient(120deg, transparent 0%, ${alpha('#ffffff', 0.42)} 50%, transparent 100%)`,
                                                    transform: 'translateX(-150%)',
                                                    animation: `${profileShimmer} 3.8s ease-in-out infinite`,
                                                    pointerEvents: 'none',
                                                },
                                                '&:hover': {
                                                    bgcolor: alpha('#ffffff', 0.28),
                                                    color: '#ffffff',
                                                    borderColor: alpha('#ffffff', 0.72),
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: `0 6px 18px ${alpha('#000000', 0.28)}, inset 0 1px 0 ${alpha('#ffffff', 0.35)}`,
                                                },
                                            }, children: [_jsx(Box, { "aria-hidden": true, sx: {
                                                        width: 8,
                                                        height: 8,
                                                        mr: 0.9,
                                                        borderRadius: '50%',
                                                        flexShrink: 0,
                                                        bgcolor: '#4caf50',
                                                        animation: `${profilePulse} 1.8s ease-in-out infinite`,
                                                    } }), _jsx(Box, { component: "span", sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }, children: profile.firstName || profileName || 'Profile' })] }), _jsxs(Menu, { anchorEl: profileMenuAnchor, open: Boolean(profileMenuAnchor), onClose: () => setProfileMenuAnchor(null), disableScrollLock: true, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, transitionDuration: { enter: 240, exit: 150 }, PaperProps: {
                                                elevation: 0,
                                                sx: {
                                                    overflow: 'hidden',
                                                    minWidth: 288,
                                                    mt: 1.25,
                                                    borderRadius: 2.5,
                                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                                    boxShadow: `0 18px 50px ${alpha(theme.palette.primary.dark, 0.22)}`,
                                                },
                                            }, MenuListProps: { sx: { p: 0 } }, children: [_jsx(Box, { sx: {
                                                        px: 2.25,
                                                        py: 1.5,
                                                        color: '#ffffff',
                                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                                                    }, children: _jsx(Typography, { sx: { fontWeight: 800, fontSize: '1.02rem', lineHeight: 1.25 }, children: "Profile" }) }), _jsxs(Box, { sx: { px: 2.25, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1.4 }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: {
                                                                        color: 'text.secondary',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.07em',
                                                                        fontWeight: 700,
                                                                        fontSize: '0.6rem',
                                                                        mb: 0.25,
                                                                    }, children: "Name" }), _jsx(Typography, { sx: { color: 'primary.dark', fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.3 }, children: profileName || profile.firstName || '—' })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: {
                                                                        color: 'text.secondary',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.07em',
                                                                        fontWeight: 700,
                                                                        fontSize: '0.6rem',
                                                                        mb: 0.25,
                                                                    }, children: "Email" }), _jsx(Typography, { sx: {
                                                                        color: 'primary.dark',
                                                                        fontWeight: 600,
                                                                        fontSize: '0.9rem',
                                                                        lineHeight: 1.3,
                                                                        wordBreak: 'break-all',
                                                                    }, children: profile.email || '—' })] })] }), _jsx(Divider, { sx: { borderColor: alpha(theme.palette.primary.main, 0.1) } }), _jsx(Box, { sx: { p: 1.25, pb: 0 }, children: _jsx(Button, { fullWidth: true, onClick: handleOpenProfile, startIcon: _jsx(AssignmentIndRoundedIcon, {}), sx: {
                                                            justifyContent: 'flex-start',
                                                            px: 1.5,
                                                            py: 1,
                                                            borderRadius: 2,
                                                            fontWeight: 700,
                                                            textTransform: 'none',
                                                            color: theme.palette.primary.dark,
                                                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                                                            transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                                                            '& .MuiButton-startIcon': { transition: 'transform 0.2s ease' },
                                                            '&:hover': {
                                                                bgcolor: theme.palette.primary.main,
                                                                color: '#ffffff',
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
                                                            },
                                                            '&:hover .MuiButton-startIcon': { transform: 'translateX(3px)' },
                                                        }, children: "Profile" }) }), _jsx(Box, { sx: { p: 1.25 }, children: _jsx(Button, { fullWidth: true, onClick: handleLogout, startIcon: _jsx(LogoutRoundedIcon, {}), sx: {
                                                            justifyContent: 'flex-start',
                                                            px: 1.5,
                                                            py: 1,
                                                            borderRadius: 2,
                                                            fontWeight: 700,
                                                            textTransform: 'none',
                                                            color: theme.palette.error.main,
                                                            bgcolor: alpha(theme.palette.error.main, 0.06),
                                                            transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                                                            '& .MuiButton-startIcon': { transition: 'transform 0.2s ease' },
                                                            '&:hover': {
                                                                bgcolor: theme.palette.error.main,
                                                                color: '#ffffff',
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: `0 8px 20px ${alpha(theme.palette.error.main, 0.35)}`,
                                                            },
                                                            '&:hover .MuiButton-startIcon': { transform: 'translateX(3px)' },
                                                        }, children: logoutButtonLabel }) })] })] })), loggedIn && !profile && !isMobile && (_jsx(Button, { type: "button", variant: "outlined", onClick: handleLogout, sx: {
                                        ...cms.navPrimary,
                                        color: '#ffffff',
                                        borderColor: alpha('#ffffff', 0.55),
                                        fontWeight: 600,
                                        px: 2,
                                        py: 0.65,
                                        borderRadius: 999,
                                        '&:hover': {
                                            borderColor: alpha('#ffffff', 0.85),
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                        },
                                    }, children: logoutButtonLabel })), isMobile && (_jsxs(_Fragment, { children: [_jsx(IconButton, { onClick: () => setDrawerOpen(true), sx: { color: '#ffffff' }, children: _jsx(MenuIcon, {}) }), _jsxs(Drawer, { anchor: "right", open: drawerOpen, onClose: () => setDrawerOpen(false), PaperProps: { sx: { width: 280 } }, children: [_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', p: 1 }, children: _jsx(IconButton, { onClick: () => setDrawerOpen(false), children: _jsx(CloseIcon, {}) }) }), _jsxs(Box, { sx: { px: 2, pt: 1 }, children: [_jsx(TextField, { placeholder: searchPlaceholder || 'Search ...', size: "small", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), onKeyDown: (e) => {
                                                                if (e.key === 'Enter')
                                                                    e.preventDefault();
                                                            }, fullWidth: true, InputProps: {
                                                                startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchIcon, {}) })),
                                                            } }), searchQuery.trim().length > 0 && (_jsx(Box, { sx: { mt: 1, maxHeight: 320, overflow: 'auto', border: '1px solid rgba(0,40,85,0.12)', borderRadius: 1 }, children: _jsx(SearchResultsList, { buckets: searchBuckets, onSelect: (hit) => {
                                                                    runNavIntent(hit.href);
                                                                    setSearchQuery('');
                                                                    setDrawerOpen(false);
                                                                }, sectionLabels: {
                                                                    departments: shell.heroSearchSectionDepartments,
                                                                    agencies: shell.heroSearchSectionAgencies,
                                                                    permits: shell.heroSearchSectionPermits,
                                                                }, selectHint: shell.heroSearchSelectHint, noResultsMessage: shell.heroSearchNoResults, applyNowLabel: shell.heroSearchApplyNow }) }))] }), _jsxs(List, { children: [loggedIn && profile && (_jsxs(ListItem, { sx: {
                                                                display: 'block',
                                                                px: 2,
                                                                pt: 1.5,
                                                                pb: 1,
                                                                mb: 0.5,
                                                                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                                            }, children: [_jsx(Typography, { variant: "caption", sx: {
                                                                        display: 'block',
                                                                        color: 'text.secondary',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.06em',
                                                                        fontWeight: 700,
                                                                        fontSize: '0.62rem',
                                                                    }, children: "Signed in as" }), _jsx(Typography, { sx: { fontWeight: 700, color: 'primary.dark', lineHeight: 1.25, mt: 0.25 }, children: profileName || profile.firstName || '—' }), profile.email ? (_jsx(Typography, { variant: "body2", sx: { color: 'text.secondary', wordBreak: 'break-all' }, children: profile.email })) : null] })), loggedIn && profile && (_jsx(ListItem, { disablePadding: true, children: _jsxs(ListItemButton, { onClick: handleOpenProfile, children: [_jsx(AssignmentIndRoundedIcon, { fontSize: "small", sx: { mr: 1.25, color: 'primary.main' } }), _jsx(ListItemText, { primary: "Profile" })] }) })), showDashboard && dashboardResolved && (_jsxs(_Fragment, { children: [_jsx(ListItem, { sx: { pt: 1.25, pb: 0.75, px: 2 }, children: _jsx(Typography, { variant: "overline", sx: {
                                                                            fontWeight: 800,
                                                                            color: 'primary.main',
                                                                            letterSpacing: '0.1em',
                                                                            fontSize: '0.65rem',
                                                                        }, children: dashboardResolved.label }) }), dashboardResolved.items.map((it) => (_jsx(ListItem, { sx: { px: 2, py: 0.35 }, disablePadding: true, children: _jsx(Paper, { elevation: 0, sx: {
                                                                            width: '100%',
                                                                            borderRadius: 2,
                                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                                                                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                                                                            overflow: 'hidden',
                                                                        }, children: _jsxs(ListItemButton, { onClick: () => {
                                                                                setDrawerOpen(false);
                                                                                runDashboardNav(it.href);
                                                                            }, sx: {
                                                                                py: 1.35,
                                                                                px: 1.5,
                                                                                gap: 1.25,
                                                                                alignItems: 'center',
                                                                                '&:hover': {
                                                                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                                                },
                                                                            }, children: [_jsx(Box, { "aria-hidden": true, sx: {
                                                                                        flexShrink: 0,
                                                                                        width: 40,
                                                                                        height: 40,
                                                                                        borderRadius: 1.25,
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        bgcolor: alpha(theme.palette.common.white, 0.85),
                                                                                        color: 'primary.main',
                                                                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                                                                    }, children: _jsx(DashboardNavItemIcon, { label: it.label }) }), _jsx(ListItemText, { primary: it.label, primaryTypographyProps: {
                                                                                        variant: 'body2',
                                                                                        fontWeight: 700,
                                                                                        color: 'primary.dark',
                                                                                        letterSpacing: '-0.01em',
                                                                                    } })] }) }) }, it.href)))] })), centerNavLinks.map((link, i) => (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemButton, { selected: isNavLinkActive(link.href), onClick: () => {
                                                                    setDrawerOpen(false);
                                                                    handleSectionNavClick(link.href);
                                                                }, children: _jsx(ListItemText, { primary: link.label }) }) }, i))), showLogin && (_jsx(ListItem, { sx: { mt: 2 }, children: _jsx(Button, { type: "button", onClick: () => {
                                                                    setDrawerOpen(false);
                                                                    const customerLoginUrl = preferredCustomerLoginUrl();
                                                                    if (customerLoginUrl)
                                                                        startLogin(customerLoginUrl);
                                                                    else if (loginHref)
                                                                        window.location.assign(withAuthCallbackUrl(loginHref));
                                                                }, variant: "contained", fullWidth: true, sx: {
                                                                    fontWeight: 600,
                                                                    bgcolor: '#2e7d32',
                                                                    color: '#ffffff',
                                                                    '&:hover': { bgcolor: '#1b5e20', color: '#ffffff' },
                                                                }, children: loginButtonLabel }) })), ctaLabel && !loggedIn && (_jsx(ListItem, { sx: { mt: 2 }, children: _jsx(Button, { href: signUpHref ? withAuthCallbackUrl(signUpHref) : '#', variant: "contained", color: "secondary", fullWidth: true, sx: {
                                                                    fontWeight: 600,
                                                                    color: '#ffffff',
                                                                    '&:hover': { bgcolor: theme.palette.secondary.dark, color: '#ffffff' },
                                                                }, children: ctaLabel }) })), loggedIn && (_jsx(ListItem, { sx: { mt: ctaLabel && !loggedIn ? 1 : 2 }, children: _jsx(Button, { type: "button", variant: "outlined", fullWidth: true, startIcon: _jsx(LogoutRoundedIcon, {}), onClick: () => handleLogout(), sx: {
                                                                    fontWeight: 700,
                                                                    color: theme.palette.error.main,
                                                                    borderColor: alpha(theme.palette.error.main, 0.5),
                                                                    '&:hover': {
                                                                        borderColor: theme.palette.error.main,
                                                                        bgcolor: alpha(theme.palette.error.main, 0.06),
                                                                    },
                                                                }, children: logoutButtonLabel }) }))] })] })] }))] })] }) }), _jsx(Box, { "aria-hidden": true, sx: { height: layoutOffset, flexShrink: 0, pointerEvents: 'none' } }), _jsx(LoginPortalDialog, { open: loginDialogOpen, onClose: closeLoginDialog, options: authOptions, embedLoginUrl: embedLoginUrl, onEmbedBack: () => setEmbedLoginUrl(null), onSelectOption: startLogin })] }));
};
export default NavBar;
